import React, { createContext, useContext, useState, useEffect } from 'react';

const QuoteContext = createContext();

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

export const QuoteProvider = ({ children }) => {
  const [quoteItems, setQuoteItems] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('flowingo_quote_list');
    if (saved) {
      try {
        setQuoteItems(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading quote list:', e);
      }
    }
    setIsInitialized(true);
  }, []);

  // Save to localStorage whenever items change (but only after initialization)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('flowingo_quote_list', JSON.stringify(quoteItems));
    }
  }, [quoteItems, isInitialized]);

  const addToQuote = (product, variant = null, quantity = 1) => {
    setQuoteItems(prev => {
      // Helper to get artikelnummer (handles both Artikelnummer and artikelnummer)
      const getArtikelnummer = (v) => {
        if (!v) return null;
        return v.Artikelnummer || v.artikelnummer || null;
      };
      
      const variantArtNr = getArtikelnummer(variant);
      
      // Check if this exact product/variant combo already exists
      const existingIndex = prev.findIndex(item => {
        if (item.productId !== product.id) return false;
        
        const itemArtNr = getArtikelnummer(item.variant);
        
        // If both have variants, compare artikelnummer
        if (variant && item.variant) {
          return itemArtNr === variantArtNr;
        }
        
        // If neither has variant
        if (!variant && !item.variant) {
          return true;
        }
        
        return false;
      });

      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      // Add new item
      return [...prev, {
        id: `${product.id}-${variantArtNr || 'base'}-${Date.now()}`,
        productId: product.id,
        productName_sv: product.name_sv,
        productName_en: product.name_en,
        productImage: product.image_url || null,
        variant: variant,
        quantity: quantity,
        addedAt: new Date().toISOString()
      }];
    });
  };

  const removeFromQuote = (itemId) => {
    setQuoteItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromQuote(itemId);
      return;
    }
    setQuoteItems(prev => 
      prev.map(item => 
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  };

  const clearQuote = () => {
    setQuoteItems([]);
  };

  const isInQuote = (productId, variantArticle = null) => {
    return quoteItems.some(item => {
      if (item.productId !== productId) return false;
      
      if (variantArticle) {
        const itemArtNr = item.variant?.Artikelnummer || item.variant?.artikelnummer;
        return itemArtNr === variantArticle;
      }
      
      return true;
    });
  };

  const getItemCount = () => {
    return quoteItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <QuoteContext.Provider value={{
      quoteItems,
      addToQuote,
      removeFromQuote,
      updateQuantity,
      clearQuote,
      isInQuote,
      getItemCount
    }}>
      {children}
    </QuoteContext.Provider>
  );
};

export default QuoteContext;
